import { Briefcase, ListChecks, Pencil, Plus, Sparkles, Trash2 } from "lucide-react";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader } from "@/components/common/loader";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { deleteCase } from "@/features/cases/api";
import { CaseDueBadge } from "@/features/cases/components/case-due-badge";
import { CaseFormDialog } from "@/features/cases/components/case-form-dialog";
import { CaseStatusBadge } from "@/features/cases/components/case-status-badge";
import { CaseStepsDialog } from "@/features/cases/components/case-steps-dialog";
import { useCases } from "@/features/cases/hooks/use-cases";
import { type Case, type CaseFilter, CASE_FILTERS } from "@/features/cases/types";
import { AdvisorWizard } from "@/features/notices/components/advisor/advisor-wizard";
import type { ClientType } from "@/features/notices/components/advisor/advisor-machine";
import { formatCents } from "@/lib/utils";

export function CasesSection({
  clientId,
  clientType = "I",
}: {
  clientId: string;
  clientType?: ClientType;
}) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<CaseFilter>("all");
  const { cases, loading, error, refetch } = useCases(clientId, filter);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Case | null>(null);
  const [managingSteps, setManagingSteps] = useState<Case | null>(null);
  const [deleting, setDeleting] = useState<Case | null>(null);
  const [advisorOpen, setAdvisorOpen] = useState(false);

  function openAdd() {
    setEditing(null);
    setDialogOpen(true);
  }

  function openEdit(c: Case) {
    setEditing(c);
    setDialogOpen(true);
  }

  async function confirmDelete() {
    if (!deleting) return;
    try {
      await deleteCase(deleting.id);
      toast.success("Case removed");
      refetch();
    } catch {
      toast.error("Couldn't remove case.");
    } finally {
      setDeleting(null);
    }
  }

  return (
    <Card className="gap-0 overflow-hidden p-0">
      <div className="bg-muted/40 flex flex-wrap items-center justify-between gap-3 border-b px-6 py-4">
        <h3 className="text-foreground flex items-center gap-2 text-lg font-bold">
          <Briefcase className="text-primary size-5" /> Cases
        </h3>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setAdvisorOpen(true)}
            title="Guided resolution recommendation that opens a case"
          >
            <Sparkles className="size-4" /> Case Advisor
          </Button>
          <Button size="sm" onClick={openAdd}>
            <Plus className="size-4" /> Open Case
          </Button>
        </div>
      </div>

      <div className="border-b px-6 py-3">
        <Tabs value={filter} onValueChange={(v) => setFilter(v as CaseFilter)}>
          <TabsList>
            {CASE_FILTERS.map((f) => (
              <TabsTrigger key={f.value} value={f.value}>
                {f.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader />
        </div>
      ) : error ? (
        <p className="text-destructive px-6 py-8 text-sm">{error}</p>
      ) : cases.length === 0 ? (
        <p className="text-muted-foreground px-6 py-8 text-sm">
          {filter === "all"
            ? "No cases opened for this client yet."
            : "No cases match this filter."}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Resolution</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Balance</TableHead>
              <TableHead>Tax Years</TableHead>
              <TableHead>Assigned</TableHead>
              <TableHead>Due</TableHead>
              <TableHead>Open Steps</TableHead>
              <TableHead className="w-32 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {cases.map((c) => (
              <TableRow key={c.id}>
                <TableCell className="font-medium">
                  <Link
                    to={`/client/${clientId}/cases/${c.id}`}
                    className="hover:text-primary hover:underline"
                  >
                    {c.resolutionTypeDisplay}
                  </Link>
                </TableCell>
                <TableCell>
                  <CaseStatusBadge status={c.status} label={c.statusDisplay} />
                </TableCell>
                <TableCell>{formatCents(c.balanceCents)}</TableCell>
                <TableCell>{c.taxYears || "—"}</TableCell>
                <TableCell>{c.assignedPractitionerName || "—"}</TableCell>
                <TableCell className="text-sm">
                  <CaseDueBadge status={c.status} days={c.daysUntilDue} />
                </TableCell>
                <TableCell>{c.openStepCount}</TableCell>
                <TableCell className="text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Steps for ${c.resolutionTypeDisplay}`}
                    onClick={() => setManagingSteps(c)}
                  >
                    <ListChecks className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Edit ${c.resolutionTypeDisplay}`}
                    onClick={() => openEdit(c)}
                  >
                    <Pencil className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${c.resolutionTypeDisplay}`}
                    onClick={() => setDeleting(c)}
                  >
                    <Trash2 className="text-destructive size-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <CaseFormDialog
        clientId={clientId}
        caseRecord={editing}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSaved={refetch}
      />

      <AdvisorWizard
        clientId={clientId}
        clientType={clientType}
        open={advisorOpen}
        onOpenChange={setAdvisorOpen}
        // Logging a notice isn't a Cases action — send the user to IRS Notices.
        onLogNotice={() => navigate(`/client/${clientId}/irs-notices`)}
        onOpened={(caseId) => {
          refetch();
          navigate(`/client/${clientId}/cases/${caseId}`);
        }}
      />

      {managingSteps && (
        <CaseStepsDialog
          key={managingSteps.id}
          caseRecord={managingSteps}
          onOpenChange={(open) => !open && setManagingSteps(null)}
          onChanged={refetch}
        />
      )}

      <AlertDialog open={deleting !== null} onOpenChange={(open) => !open && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove case?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the {deleting?.resolutionTypeDisplay} case from this client. It
              stays on historical records.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete}>Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
